import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Check, Link2, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { API } from "@/App";
import api from "@/api/axios";

const EMPTY_STATUS = { configured: true, connected: false, email: null };

export default function GoogleCalendarConnect({ onChange, onStatusChange }) {
  const [status, setStatus] = useState(EMPTY_STATUS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  // Held in a ref so fetchStatus does not need it as a dependency — callers
  // usually pass an inline arrow, which would otherwise re-run the fetch on
  // every render of the parent.
  const statusCallback = useRef(onStatusChange);
  useEffect(() => {
    statusCallback.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`${API}/google/status`, { silent: true });
      if (!mounted.current) return;
      const next = {
        configured: data?.configured !== false,
        connected: !!data?.connected,
        email: data?.email || null,
      };
      setStatus(next);
      statusCallback.current?.(next);
    } catch (err) {
      // 401 / 500 / network — degrade quietly to the not-connected state.
      console.error("Google Calendar status check failed:", err);
      if (mounted.current) {
        setStatus(EMPTY_STATUS);
        statusCallback.current?.(EMPTY_STATUS);
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`${API}/google/auth-url`);
      const url = data?.url;
      if (!url) throw new Error("No auth URL returned");
      // Full redirect on purpose: popups get blocked and Google refuses
      // to render the consent screen inside some embedded webviews.
      window.location.href = url;
    } catch (err) {
      console.error("Google Calendar auth URL failed:", err);
      if (mounted.current) setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      await api.post(`${API}/google/disconnect`);
      toast.success("Google Calendar disconnected");
      await fetchStatus();
      onChange?.();
    } catch (err) {
      console.error("Google Calendar disconnect failed:", err);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking Google Calendar…
      </div>
    );
  }

  if (!status.configured) {
    return (
      <p className="px-3 text-xs text-muted-foreground">
        Google Calendar isn't configured for this workspace yet.
      </p>
    );
  }

  if (status.connected) {
    return (
      <Card className="shadow-none">
        <CardContent className="flex items-center gap-3 p-2.5">
          <Calendar size={16} className="shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Badge
                variant="outline"
                className="gap-1 border-emerald-600/30 px-1.5 py-0 font-medium text-emerald-700"
              >
                <Check className="h-3 w-3" />
                Connected
              </Badge>
            </div>
            <p className="mt-0.5 max-w-[220px] truncate text-xs text-muted-foreground">
              {status.email || "Google account"}
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDisconnect}
            disabled={busy}
            className="text-muted-foreground hover:text-foreground"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Disconnect
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-dashed shadow-none">
      <CardContent className="flex items-center gap-3 p-2.5">
        <Calendar size={16} className="shrink-0 text-muted-foreground" />
        <p className="hidden text-xs text-muted-foreground sm:block">
          Sync meetings with Google Calendar
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={handleConnect}
          disabled={busy}
          className="border-slate-300 bg-white font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Link2 className="h-3.5 w-3.5 text-[#4285F4]" />
          )}
          Connect Google Calendar
        </Button>
      </CardContent>
    </Card>
  );
}
